// Turns the deployer's structured log output into human-readable lines.
//
// In production the deployer logs Go slog JSON — one object per line — so the
// Logs tab would otherwise show a wall of `{"time":…,"level":…,"msg":…}`. Each
// line is parsed into the same shapes the deployer's own text handler renders
// (internal/logx/texthandler.go): an ordinary event becomes time + level +
// message + key=value attrs, and the wide "step completed" event becomes a
// headline plus an aligned block of its correlation attrs.
//
// Anything that isn't a slog object — plain stdout from a tool the deployer
// shells out to, or the truncated trailing object of a still-streaming poll —
// falls back to the original text. That tool output (tofu especially) is often
// ANSI-colored; lib/ansi turns the escapes into styled segments rather than
// letting `[0m [1m` noise through, and strips them from slog messages and attrs.
//
// The one exception is a machine-readable JSON dump (`isJsonDump`): the deployer
// once teed `tofu show -json` into its step logs, and archived logs still carry
// that as a single, often multi-megabyte line of plan JSON — including plaintext
// sensitive values. Such a line becomes a `hidden` placeholder carrying only its
// size, deliberately with no way to expand it; the formatted view, the raw view
// and copy-to-clipboard all go through `redactJsonDumps` / `hiddenPlaceholder`.
// A slog record is never hidden — it is already formatted.

import { AnsiSegment, hasAnsi, parseAnsi, stripAnsi } from './ansi';

/** One rendered attribute. Values on a narration line are pre-quoted. */
export interface LogAttr {
  key: string;
  value: string;
}

export interface SlogLine {
  kind: 'slog';
  time: string;
  level: string;
  msg: string;
  attrs: LogAttr[];
}

export interface StepEventLine {
  kind: 'step';
  time: string;
  outcome: 'success' | 'error';
  headline: string;
  details: LogAttr[];
}

export interface RawLine {
  kind: 'raw';
  /** The line with any ANSI escapes removed. */
  text: string;
  /** Styled runs, present only when the line carried ANSI escapes. */
  segments?: AnsiSegment[];
}

/** A JSON dump withheld from display; only its size survives. */
export interface HiddenLine {
  kind: 'hidden';
  /** Length of the withheld line, in characters. */
  chars: number;
}

export type FormattedLine = SlogLine | StepEventLine | RawLine | HiddenLine;

/** A non-slog line longer than this that opens with `{` or `[` is a JSON dump. */
export const JSON_DUMP_MIN_CHARS = 2000;

// MIRRORED RULE: the getLogs Lambda applies this same dump rule server-side
// (terraform/websocket/functions/get_logs/redact.go) before sending logs —
// JSON_DUMP_MIN_CHARS, TOFU_PLAN_MARKERS, looksLikeDump, isJsonDump, humanSize and
// hiddenPlaceholder must stay in sync with it.
/**
 * Keys only a tofu/terraform plan or state JSON carries. Matched anywhere in a
 * line, whatever its length or first character, because a tailed or split line
 * can start mid-object.
 */
const TOFU_PLAN_MARKERS = [
  '"resource_changes"',
  '"before_sensitive"',
  '"after_sensitive"',
  '"planned_values"',
  '"prior_state"',
];

/** Dump heuristics on a line already known not to be a slog record. */
const looksLikeDump = (line: string): boolean => {
  if (TOFU_PLAN_MARKERS.some(m => line.includes(m))) return true;
  if (line.length <= JSON_DUMP_MIN_CHARS) return false;
  const trimmed = (hasAnsi(line) ? stripAnsi(line) : line).trim();
  return trimmed.startsWith('{') || trimmed.startsWith('[');
};

/** Human-size a character count: 512 B, 3.4 KB, 1.4 MB. */
export function humanSize(chars: number): string {
  if (chars < 1024) return `${chars} B`;
  if (chars < 1024 * 1024) return `${(chars / 1024).toFixed(1)} KB`;
  return `${(chars / (1024 * 1024)).toFixed(1)} MB`;
}

/** The text that stands in for a hidden JSON dump, on screen and in copies. */
export const hiddenPlaceholder = (chars: number): string =>
  `⋯ ${humanSize(chars)} of JSON output hidden (machine-readable tool output, may contain secrets)`;

/** The one wide event every workflow step emits; rendered as a block. */
const STEP_COMPLETED_MSG = 'step completed';

/** Attrs the step-completed headline already states, so the block skips them. */
const BLOCK_CONSUMED = new Set(['action', 'outcome', 'error', 'duration_ms']);

const pad2 = (n: number): string => String(n).padStart(2, '0');

/** Local HH:mm:ss for an RFC3339 timestamp; '' when it isn't a usable date. */
const formatTime = (time: unknown): string => {
  if (typeof time !== 'string' && typeof time !== 'number') return '';
  const d = new Date(time);
  if (Number.isNaN(d.getTime())) return '';
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}`;
};

const isPlainObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

/** Text form of an attr value: strings as-is, scalars stringified, rest JSON. */
const renderValue = (v: unknown): string => {
  if (typeof v === 'string') return stripAnsi(v);
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  return JSON.stringify(v) ?? '';
};

/** Flatten nested objects into dotted keys (`meta: {commit}` → `meta.commit`). */
const flattenAttrs = (
  obj: Record<string, unknown>,
  prefix: string,
  out: LogAttr[]
): LogAttr[] => {
  Object.keys(obj).forEach(key => {
    if (prefix === '' && (key === 'time' || key === 'level' || key === 'msg')) return;
    const value = obj[key];
    if (isPlainObject(value)) {
      flattenAttrs(value, `${prefix}${key}.`, out);
      return;
    }
    out.push({ key: `${prefix}${key}`, value: renderValue(value) });
  });
  return out;
};

/** Quote a narration value that is empty or has whitespace, so it stays legible. */
const quoteIfNeeded = (s: string): string => (s === '' || /\s/.test(s) ? `"${s}"` : s);

const lookup = (attrs: LogAttr[], key: string): string =>
  attrs.find(a => a.key === key)?.value ?? '';

/**
 * Render a duration_ms attr as seconds with one decimal (5000 → '5.0s'). A
 * non-numeric value keeps its raw text with a 'ms' suffix so nothing is lost.
 */
export function humanDuration(durationMs: unknown): string {
  let ms = NaN;
  if (typeof durationMs === 'number') ms = durationMs;
  else if (typeof durationMs === 'string' && durationMs.trim() !== '') ms = Number(durationMs);
  if (!Number.isFinite(ms)) return `${durationMs ?? ''}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

/** A slog record needs a time plus string level and msg to render as one. */
const isSlogRecord = (obj: unknown): obj is Record<string, unknown> =>
  isPlainObject(obj) &&
  typeof obj.msg === 'string' &&
  typeof obj.level === 'string' &&
  obj.time !== undefined;

/** Parse a line as a slog record, or undefined when it isn't one. */
const parseSlog = (original: string): Record<string, unknown> | undefined => {
  const trimmed = original.trim();
  // Only pay for JSON.parse on something that could be a whole object — the
  // trailing line of a live poll is often a half-written one.
  if (!trimmed.startsWith('{') || !trimmed.endsWith('}')) return undefined;
  try {
    const parsed: unknown = JSON.parse(trimmed);
    return isSlogRecord(parsed) ? parsed : undefined;
  } catch {
    return undefined;
  }
};

/**
 * True for a machine-readable JSON dump that must not be rendered: a non-slog
 * line over JSON_DUMP_MIN_CHARS whose trimmed text starts with `{` or `[`, or
 * any non-slog line carrying a tofu-plan key. A slog record is never a dump.
 */
export function isJsonDump(line: string): boolean {
  return looksLikeDump(line) && parseSlog(line) === undefined;
}

/** Replace every JSON-dump line of a (plain-text) log with its placeholder. */
export function redactJsonDumps(text: string): string {
  return text
    .split('\n')
    .map(line => (isJsonDump(line) ? hiddenPlaceholder(line.length) : line))
    .join('\n');
}

const rawLine = (original: string): RawLine =>
  hasAnsi(original)
    ? { kind: 'raw', text: stripAnsi(original), segments: parseAnsi(original) }
    : { kind: 'raw', text: original };

const formatLine = (original: string): FormattedLine => {
  // JSON is parsed from the original text: a slog value's escapes are
  // JSON-encoded (`\u001b[1m`), and stripping the line first would cut them in
  // half. Values are cleaned after parsing; only non-JSON output is stripped here.
  const parsed = parseSlog(original);
  if (!parsed) {
    return looksLikeDump(original)
      ? { kind: 'hidden', chars: original.length }
      : rawLine(original);
  }

  const time = formatTime(parsed.time);
  const level = parsed.level as string;
  const msg = stripAnsi(parsed.msg as string);
  const attrs = flattenAttrs(parsed, '', []);

  if (msg === STEP_COMPLETED_MSG) {
    const action = lookup(attrs, 'action');
    const outcome =
      lookup(attrs, 'outcome') === 'error' || level.toUpperCase() === 'ERROR'
        ? 'error'
        : 'success';
    const headline =
      outcome === 'error'
        ? `✘ ${action} failed: ${lookup(attrs, 'error')}`
        : `✔ ${action} succeeded in ${humanDuration(parsed.duration_ms)}`;
    return {
      kind: 'step',
      time,
      outcome,
      headline,
      details: attrs.filter(a => !BLOCK_CONSUMED.has(a.key)),
    };
  }

  return {
    kind: 'slog',
    time,
    level,
    msg,
    attrs: attrs.map(a => ({ key: a.key, value: quoteIfNeeded(a.value) })),
  };
};

export function formatLogText(raw: string): FormattedLine[] {
  const lines = raw.split('\n');
  // A log blob normally ends in a newline; don't render the empty tail it makes.
  if (lines.length > 1 && lines[lines.length - 1] === '') lines.pop();
  return lines.map(formatLine);
}
